import { Grid } from '../../../../Common/Grid'
import {
  Button,
  Checkbox,
  Divider,
  IconButton,
  Input,
  Typography
} from '@fullscreendigital/carrefour-dex-fe'
import { useEffect, useMemo, useState } from 'react'
import { getValidationSchema, getInitialValues, conformOptions } from './helpers'
import { useFormik } from 'formik'
import { formatErrorMessage } from '../../../../../../utils/formatting'
import { useNotification } from '../../../../../../hooks'
import { TitleHeader } from '../../../Common/TitleHeader'
import { ManageSearch as ManageSearchIcon } from '@mui/icons-material'
import { FlexCenter } from '../../../../Common/FlexCenter'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../utils/api'
import { useLocation, useNavigate } from 'react-router-dom'
import AlertModal from '../../../Common/AlertModal/AlertModal'
import { URLS } from '../../../constants'
import ImageUpload from 'components/shop-reception/mobile/Common/ImageUpload/ImageUpload'
import { CircularProgress } from '@mui/material'
import { useOfflineElementTool } from 'components/core-ui/Offline'
import { ScrollTopButton } from 'components/core-ui/ScrollTopButton'

const SupportScanStep1 = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const location = useLocation()
  const id = location.state?.item?.id || location.state

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [isFinalizing, setIsFinalizing] = useState<boolean>(true)
  const [supportImageButtonVisible, setSupportImageButtonVisible] = useState<boolean>(false)
  const [supportImageUploaded, setSupportImageUploaded] = useState<boolean>(true)
  const [defectImageVisible, setDefectImageVisible] = useState<boolean>(false)
  const [defectImageUploaded, setDefectImageUploaded] = useState<boolean>(true)
  const [supportImageId, setSupportImageId] = useState(null)
  const [defectImageId, setDefectImageId] = useState(null)
  const [supportId, setSupportId] = useState(null)
  const [supportTypeId, setSupportTypeId] = useState(null)
  const [supportData, setSupportData] = useState(null)
  const [supportNumber, setSupportNumber] = useState('')
  const [searchCode, setSearchCode] = useState('')
  const [counter, setCounter] = useState(0)

  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false)
  const [finalizeModalContent, setFinalizeModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isMoreSupportsModalOpen, setIsMoreSupportsModalOpen] = useState(false)
  const [moreSupportsModalContent, setMoreSupportsModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isPriorityProductsModalOpen, setIsPriorityProductsModalOpen] = useState(false)
  const [priorityProductsModalContent, setPriorityProductsModalContent] = useState({
    description: '',
    listItems: [],
    buttons: []
  })
  const [isSensitiveAndShortDlcModalOpen, setIsSensitiveAndShortDlcModalOpen] = useState(false)
  const [sensitiveAndShortDlcModalContent, setSensitiveAndShortDlcModalContent] = useState({
    title: '',
    listItems: [],
    buttons: []
  })
  const [isChooseSupportModalOpen, setIsChooseSupportModalOpen] = useState(false)
  const [chooseSupports, setChooseSupports] = useState([])
  const [isWrongDestinationModalOpen, setIsWrongDestinationModalOpen] = useState(false)
  const [wrongDestinationInfo, setWrongDestinationInfo] = useState({ supportId: null, message: '' })


  const { offlineData, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'SUPPORT_SCAN_STEP_1',
    url: URLS.AUTH_SUPPORT_SCAN_STEP_1,
    elementId: id
  })

  const { data: truck, isLoading } = useQuery(
    'counter',
    async () => {
      const truck = await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/counter/${id}`,
        null
      )
      setCounter(truck.counter)

      return truck
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching truck. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: supportList, isLoading: isLoadingSupports } = useQuery(
    'supportList',
    async () => {
      return await makeCoreGetRequest('/shop-reception-mobile/support/shop/principal/type', null)
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching supports. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: shopCode, isLoading: isLoadingShopCode } = useQuery(
    'shopCode',
    async () => {
      const shopList = await makeCoreGetRequest('/shop-reception-mobile/home/user-shops', null)

      const storedShopCode = localStorage.getItem('selectedStoreCode')
      if (storedShopCode) return storedShopCode

      if (shopList.length > 0) return shopList[0].shopCode
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching supports. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: depositId, isLoading: isLoadingDepositId } = useQuery(
    'depositId',
    async () => {
      const resp = await makeCoreGetRequest('/shop-reception-mobile/truck/shop/' + id, null)
      return resp.depositDocumentList[0].depositId
    },
    {
      onError: error => {
        showNotification({
          message: `Error while fetching supports. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const initialValues = useMemo(
    () => getInitialValues(supportData, offlineData?.data),
    [supportData, offlineData?.data]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        const difference = {
          shopCode: shopCode,
          idDeposit: depositId,
          idHub: null,
          idSupportTypeConfirmed: values.supportTypeId,
          idSupportTypeExpected: supportTypeId,
          noticeNumber: null,
          supportCode: values.supportId,
          truckNumber: truck?.truckNumber,
          pictureId: supportImageId
        }

        if (supportTypeId !== values.supportTypeId)
          await makeCorePostRequest(`/shop-reception-mobile/support-type-diffs`, difference)

        const resp = await makeCorePostRequest(`/shop-reception-mobile/support/shop/save/${id}`, {
          ...values,
          defectImageId,
          supportImageId
        })

        let existingData = JSON.parse(localStorage.getItem(`myData-${id}`)) || {}

        if (resp.hasSgrProducts) {
          if (!existingData[searchCode]) {
            existingData[searchCode] = {
              sgrProducts: resp.sgrProductsList
            }
          }

          localStorage.setItem(`myData-${id}`, JSON.stringify(existingData))
        }

        const localCounter = resp.counter
        if (localCounter > 0) {
          setCounter(localCounter)
        }

        if (resp.priorityProducts?.length > 0) {
          showPriorityProducts(
            resp.priorityProducts,
            resp.hasSensitiveProducts,
            resp.hasProductsWithShortDlc,
            resp.hasSgrProducts,
            resp.sgrProductsList
          )
        } else if (
          resp.hasSensitiveProducts ||
          resp.hasProductsWithShortDlc ||
          resp.hasSgrProducts
        ) {
          showSensitiveAndShortDlcInfo(
            resp.hasSensitiveProducts,
            resp.hasProductsWithShortDlc,
            resp.hasSgrProducts,
            resp.sgrProductsList
          )
        } else {
          openMoreSupportsModal(supportId)
        }

        formik.setFieldValue('nonCompliantReasonList', [])
        await saveOfflineData([
          { field: 'offlineNonCompliantReasonList', value: [] }
        ])

        await clearData()

        if (document.getElementById('scan-input')) {
          document.getElementById('scan-input').focus()
        }
      } catch (error) {
        showNotification({
          message: `A aparut o eroare la salvarea datelor. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const clearData = async () => {
    setIsSearching(false)
    setShowConfirmation(false)
    setSupportImageUploaded(true)
    setDefectImageUploaded(true)
    setSupportImageButtonVisible(false)
    setDefectImageVisible(false)
    setDefectImageId(null)
    setSupportImageId(null)
    setSupportTypeId(null)
    setIsFinalizing(true)

    await deleteOfflineData()
  }

  useEffect(() => {
    ;(async () => {
      if (offlineData?.data?.offlineSupportCode) {
        const {
          offlineSupportCode,
          offlineSupportTypeId,
          offlineSupportImageId,
          offlineDefectImageVisible,
          offlineNonCompliantReasonList,
          offlineDefectImageUploaded,
          offlineDefectImageId
        } = offlineData.data

        // support code search
        setSearchCode(offlineSupportCode)
        await toggleIsSearching(formik.values.supportCode.trim())

        // support type
        if (
          offlineSupportTypeId &&
          offlineSupportTypeId !== supportTypeId &&
          !offlineSupportImageId
        ) {
          setSupportImageButtonVisible(true)
          setSupportImageUploaded(false)
        } else {
          setSupportImageButtonVisible(false)
          setSupportImageUploaded(true)
        }
        if (offlineSupportImageId) {
          setSupportImageId(offlineSupportImageId)
        }

        // compliant reasons
        if (offlineNonCompliantReasonList) {
          setShowConfirmation(true)
        }
        setDefectImageVisible(offlineDefectImageVisible && !offlineDefectImageUploaded)
        setDefectImageUploaded(offlineDefectImageUploaded)
        if (offlineDefectImageId) {
          setDefectImageId(offlineDefectImageId)
        }
      }
    })()
  }, [offlineData, formik.values?.supportCode])

  const toggleIsSearching = async (rawCode: string) => {
    if (!rawCode) { return }

    try {
      formik.setFieldError('supportCode', undefined);

      if (rawCode.length > 20) {
        formik.setFieldError(
          'supportCode',
          'Codul suport nu poate depăși 20 caractere'
        )
        return
      }

      if (!/^[A-Za-z0-9]+$/.test(rawCode)) {
        formik.setFieldError('supportCode', 'Cod invalid pentru suport')
        formik.setFieldValue('supportCode', '')
        return
      }

      const result = await makeCorePostRequest(
        `/shop-reception-mobile/support/shop/scan-by-code/${id}`,
        {
          supportCode: rawCode
        }
      )

      if (result.mustChoose) {
        setChooseSupports(result.supportList)
        setIsChooseSupportModalOpen(true)
      } else if (result.wrongDestination) {
        setWrongDestinationInfo({ supportId: result.supportId, message: result.errorMessage })
        setIsWrongDestinationModalOpen(true)
      } else if (!result.scanSuccess) {
        await clearData()
        showNotification({
          message: `A aparut o eroare la cautarea codului. ${result.errorMessage}`,
          type: 'error'
        })
        return
      } else {
        setSupportNumber(result.supportNumber)
        setSupportId(result.supportId)
        setSupportTypeId(result.supportTypeId)
        setSupportData(result)
      }
    } catch (error) {
      await clearData()
      showNotification({
        message: `A aparut o eroare la cautarea codului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      formik.setFieldValue('supportCode', '')
    }

    setIsSearching(true)
    setIsFinalizing(false)
  }

  const toggleShowConfirmation = (): void => {
    setShowConfirmation(!showConfirmation)
  }

  const toggleIsFinalizing = async () => {
    try {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/summary/${id}`,
        null
      )

      const count = response['counter']
      const total = response['total']

      openFinalizeModal(count, total)
    } catch (error) {
      showNotification({
        message: `Error while submitting. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const deleteSecondarySupports = async () => {
    try {
      await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/secondary/delete?supportId=${supportId}`,
        null
      )
    } catch (error) {
      showNotification({
        message: `Error while deleting. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const handleChooseSupport = async (supportId) => {
    setIsChooseSupportModalOpen(false)

    try {
      const resp = await makeCorePostRequest(
        `/shop-reception-mobile/support/shop/scan-by-id/${id}`, { supportId }
      )

      if (resp.wrongDestination) {
        setWrongDestinationInfo({ supportId: resp.supportId, message: resp.errorMessage })
        setIsWrongDestinationModalOpen(true)
      } else if (!resp.scanSuccess) {
        showNotification({
          message: `A aparut o eroare la cautarea codului. ${resp.errorMessage}`,
          type: 'error'
        })
        return
      } else {
        setSupportNumber(resp.supportNumber)
        setSupportId(resp.supportId)
        setSupportTypeId(resp.supportTypeId)
        setSupportData(resp)
      }

    } catch (error) {
      showNotification({
        message: `A aparut o eroare la cautarea codului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }

    setIsSearching(true)
    setIsFinalizing(false)
  }

  const handleMarkWrongDestination = () => {
    setIsWrongDestinationModalOpen(false)
    makeCoreGetRequest(
      `/shop-reception-mobile/support/shop/wrong-destination/${id}/${wrongDestinationInfo.supportId}`
    ).then(() => {
      formik.setFieldValue('supportCode', '')
      clearData()

      if (document.getElementById('scan-input')) {
        document.getElementById('scan-input').focus()
      }
    }).catch(err => showNotification({ message: `Eroare: ${formatErrorMessage(err)}`, type: 'error' }))
  }

  const closeWrongDestinationModal = () => {
    setIsWrongDestinationModalOpen(false)
  }

  const handleMoveSupportInTruck = () => {
    closeWrongDestinationModal();
    formik.setFieldValue('supportCode', '');
    clearData()

    if (document.getElementById('scan-input')) {
      document.getElementById('scan-input').focus()
    }
  }

  const openFinalizeModal = (count, total) => {
    setFinalizeModalContent({
      title: '',
      description: 'Aţi scanat ' + count + ' suporţi din ' + total + ' aşteptaţi.',
      buttons: [
        {
          label: 'Continuă scanarea',
          onClick: () => {
            setIsFinalizeModalOpen(false)
            setTimeout(() => {
              if (document.getElementById('scan-input')) {
                document.getElementById('scan-input').focus()
              }
            }, 0)
          },
          color: 'primary'
        },
        {
          label: 'Am terminat',
          onClick: () => {
            navigate(URLS.AUTH_SUPPORT_SCAN_STEP_4, { state: id })
            setIsFinalizeModalOpen(false)
          },
          color: 'secondary'
        }
      ]
    })
    setIsFinalizeModalOpen(true)
  }

  const closeFinalizeModal = () => {
    setIsFinalizeModalOpen(false)
  }

  const showPriorityProducts = (
    priorityProducts,
    hasSensitiveProducts,
    hasProductsWithShortDlc,
    hasSgrProducts,
    sgrProducts
  ) => {
    setPriorityProductsModalContent({
      description: 'Există produse prioritare pentru vânzare: ',
      listItems: priorityProducts,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            if (hasSensitiveProducts || hasProductsWithShortDlc || hasSgrProducts) {
              showSensitiveAndShortDlcInfo(
                hasSensitiveProducts,
                hasProductsWithShortDlc,
                hasSgrProducts,
                sgrProducts
              )
            } else {
              openMoreSupportsModal(supportId)
            }

            setIsPriorityProductsModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsPriorityProductsModalOpen(true)
  }

  const closePriorityProductsModal = () => {
    setIsPriorityProductsModalOpen(false)
  }

  const showSensitiveAndShortDlcInfo = (
    hasSensitiveProducts,
    hasShortDlcProducts,
    hasSgrProducts,
    sgrProducts
  ) => {
    const conditions = [
      { condition: hasSensitiveProducts, message: 'produse sensibile' },
      { condition: hasShortDlcProducts, message: 'produse cu DLC' },
      { condition: hasSgrProducts, message: 'produse SGR' }
    ]

    const activeConditions = conditions.filter(c => c.condition)
    const messages = activeConditions.map(c => c.message)

    let productsMessage = ''
    if (messages.length > 0) {
      productsMessage = 'Pe acest suport există ' + messages.join(' şi ') + '!'
    }

    setSensitiveAndShortDlcModalContent({
      title: productsMessage,
      listItems: sgrProducts,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            openMoreSupportsModal(supportId)
            setIsSensitiveAndShortDlcModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsSensitiveAndShortDlcModalOpen(true)
  }

  const closeSensitiveAndShortDlcModal = () => {
    setIsSensitiveAndShortDlcModalOpen(false)
  }

  const openMoreSupportsModal = supportId => {
    setMoreSupportsModalContent({
      title: 'Există ambalaje secundare pe acest suport?',
      description: '',
      buttons: [
        {
          label: 'Nu',
          onClick: () => {
            deleteSecondarySupports()

            formik.setFieldValue('supportCode', '')
            setSupportId(null)
            setSupportNumber('')

            setIsMoreSupportsModalOpen(false)
          },
          color: 'primary'
        },
        {
          label: 'Da',
          onClick: () => {
            formik.setValues({
              supportCode: '',
              supportTypeId: '',
              reason: []
            })
            setSupportImageButtonVisible(false)
            setDefectImageVisible(false)
            setCounter(null)
            setSupportId(null)
            setSupportNumber('')

            navigate(URLS.AUTH_SUPPORT_SCAN_STEP_3, {
              state: { truckId: id, supportId: supportId }
            })
            setIsMoreSupportsModalOpen(false)
          },
          color: 'secondary'
        }
      ]
    })
    setIsMoreSupportsModalOpen(true)
  }

  const closeMoreSupportsModal = () => {
    setIsMoreSupportsModalOpen(false)
  }

  if (isLoading || isLoadingSupports || isLoadingDepositId || isLoadingShopCode) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  let items = [
    <TitleHeader title="Cod Suport" />,
    <FlexCenter>
      <Input
        type="text"
        label="Cod Suport"
        variant="standard"
        width="100%"
        autoFocus
        value={formik.values?.supportCode}
        onChange={async event => {
          formik.setFieldValue('supportCode', (event.target as HTMLInputElement).value)
          setSearchCode((event.target as HTMLInputElement).value)
          await saveOfflineData([
            { field: 'offlineSupportCode', value: (event.target as HTMLInputElement).value }
          ])
        }}
        onKeyUp={async event => {
          if (event.which == 0) {
            await toggleIsSearching((event.target as HTMLInputElement).value.trim())
          }
        }}
        error={!!formik.errors.supportCode}
        helperText={formik.errors.supportCode as string}
        id="scan-input"
      />
      <IconButton onClick={() => toggleIsSearching(formik.values.supportCode.trim())}>
        <ManageSearchIcon />
      </IconButton>
    </FlexCenter>,
    <Divider />
  ]

  if (isSearching) {
    items = [
      ...items,
      <Typography variant="medium" component="p">
        Numǎr suport: {supportNumber}
      </Typography>,
      <Input
        type="select"
        label="Tip Ambalaj"
        variant="standard"
        width="100%"
        value={formik.values.supportTypeId}
        values={supportList.map(support => ({ value: support.id, label: support.name }))}
        onChange={async event => {
          if ((event.target as HTMLInputElement).value !== supportTypeId) {
            setSupportImageButtonVisible(true)
            setSupportImageUploaded(false)
          } else {
            setSupportImageButtonVisible(false)
            setSupportImageUploaded(true)
          }

          formik.setFieldValue('supportTypeId', (event.target as HTMLInputElement).value)
          await saveOfflineData([
            { field: 'offlineSupportTypeId', value: (event.target as HTMLInputElement).value }
          ])
        }}
        error={!!formik.errors.supportTypeId}
        helperText={formik.errors.supportTypeId as string}
      />,
      supportImageButtonVisible && supportId && (
        <>
          <ImageUpload
            uploadUrl={`/shop-reception-mobile/support/shop/upload-different-support/${supportId}`}
            onSuccess={async response => {
              if (response.id) setSupportImageId(response.id)
              setSupportImageUploaded(true)
              await saveOfflineData([{ field: 'offlineSupportImageId', value: response.id }])
              showNotification({
                message: `Imaginea a fost incarcata cu succes.`,
                type: 'success'
              })
            }}
            onError={() =>
              showNotification({
                message: `A aparut o eroare la incarcarea imaginii.`,
                type: 'error'
              })
            }
            isButton
          />
        </>
      ),
      <Button
        type="button"
        variant="contained"
        label="CONFORMITATE SUPORT"
        style={{ width: '100%' }}
        onClick={toggleShowConfirmation}
      />
    ]
  }

  items = [
    ...items,
    <div style={{ display: showConfirmation ? 'block' : 'none', marginBottom: '24px' }}>
      <Grid
        hasPaper={true}
        items={conformOptions.map(option => (
          <Checkbox
            checked={formik.values.nonCompliantReasonList?.includes(option.value)}
            onChange={async () => {
              const set = new Set(formik.values.nonCompliantReasonList)
              if (set.has(option.value)) {
                set.delete(option.value)
                if (set.size < 1) {
                  setDefectImageVisible(false)
                  await saveOfflineData([{ field: 'offlineDefectImageVisible', value: false }])
                  setDefectImageUploaded(true)
                }
              } else {
                set.add(option.value)
                if (set.size >= 1 && option.value !== 'COMPLIANT') {
                  setDefectImageVisible(true)
                  await saveOfflineData([{ field: 'offlineDefectImageVisible', value: true }])
                  if (!defectImageId) setDefectImageUploaded(false)
                } else if (option.value === 'COMPLIANT') {
                  setDefectImageVisible(false);
                  setDefectImageUploaded(true);
                  await saveOfflineData([{ field: 'offlineDefectImageVisible', value: false }]);
                  await saveOfflineData([{ field: 'offlineDefectImageUploaded', value: true }]);
                }
              }

              const newList = Array.from(set)
              formik.setFieldValue('nonCompliantReasonList', newList)
              formik.setFieldValue('defect', newList.includes('COMPLIANT'))

              await saveOfflineData([
                { field: 'offlineNonCompliantReasonList', value: Array.from(set).join(',') },
                {
                  field: 'offlineDefect',
                  value: formik.values.nonCompliantReasonList?.includes('COMPLIANT')
                }
              ])
            }}
            label={option.label}
            disabled={
              (option.value !== 'COMPLIANT' &&
                formik.values.nonCompliantReasonList?.includes('COMPLIANT')) ||
              (option.value === 'COMPLIANT' &&
                formik.values.nonCompliantReasonList?.length > 0 &&
                !formik.values.nonCompliantReasonList?.includes('COMPLIANT'))
            }
          />
        ))}
        itemsPerRow={1}
      />
      <div style={{ display: defectImageVisible ? 'block' : 'none' }}>
        <ImageUpload
          uploadUrl={`/shop-reception-mobile/support/shop/upload-non-conform-support/${formik.values.supportId}`}
          onSuccess={async response => {
            if (response.id) setDefectImageId(response.id)
            setDefectImageUploaded(true)
            await saveOfflineData([
              { field: 'offlineDefectImageId', value: response.id },
              { field: 'offlineDefectImageUploaded', value: true }
            ])
            showNotification({
              message: `Imaginea a fost incarcata cu succes.`,
              type: 'success'
            })
          }}
          onError={() =>
            showNotification({
              message: `A aparut o eroare la incarcarea imaginii.`,
              type: 'error'
            })
          }
          isButton
          label="Adauga imagine suport neconform"
        />
      </div>
    </div>,
    isSearching && (
      <Button
        type="button"
        variant="contained"
        label="ALTE AMBALAJE"
        style={{ width: '100%' }}
        onClick={async () => {
          formik.handleSubmit()
          navigate(URLS.AUTH_SUPPORT_SCAN_STEP_2, {
            state: {
              supportType: formik.values.supportTypeId,
              supportId: formik.values.supportId,
              truckId: truck.truckId
            }
          })
        }}
        disabled={
          formik.values.nonCompliantReasonList?.length === 0 ||
          !supportImageUploaded ||
          !defectImageUploaded ||
          formik.isSubmitting
        }
      />
    ),
    isSearching && (
      <Button
        type="button"
        variant="contained"
        label="CONFIRM TIP SUPORT"
        style={{ width: '100%' }}
        onClick={() => formik.handleSubmit()}
        disabled={
          formik.values.nonCompliantReasonList?.length === 0 ||
          !supportImageUploaded ||
          !defectImageUploaded ||
          formik.isSubmitting
        }
      />
    ),
    <>
      <AlertModal
        open={isMoreSupportsModalOpen}
        handleClose={closeMoreSupportsModal}
        title={moreSupportsModalContent.title}
        description={moreSupportsModalContent.description}
        buttons={moreSupportsModalContent.buttons}
      />
      <AlertModal
        open={isPriorityProductsModalOpen}
        handleClose={closePriorityProductsModal}
        description={priorityProductsModalContent.description}
        listItems={priorityProductsModalContent.listItems}
        buttons={priorityProductsModalContent.buttons}
      />
      <AlertModal
        open={isSensitiveAndShortDlcModalOpen}
        handleClose={closeSensitiveAndShortDlcModal}
        title={sensitiveAndShortDlcModalContent.title}
        listItems={sensitiveAndShortDlcModalContent.listItems}
        buttons={sensitiveAndShortDlcModalContent.buttons}
      />
      <AlertModal
        open={isChooseSupportModalOpen}
        handleClose={() => setIsChooseSupportModalOpen(false)}
        title="Alege depozitul sursa:"
        listItems={chooseSupports.map(s => s.depositName)}
        buttons={chooseSupports.map(s => ({
          label: s.depositName,
          onClick: () => handleChooseSupport(s.id),
          color: 'primary'
        }))}
      />
      <AlertModal
        open={isWrongDestinationModalOpen}
        handleClose={closeWrongDestinationModal}
        description={wrongDestinationInfo.message}
        buttons={[
          {
            label: 'Păstrez',
            onClick: handleMarkWrongDestination,
            color: 'primary'
          },
          {
            label: 'Urc În camion',
            onClick: handleMoveSupportInTruck,
            color: 'secondary'
          }
        ]}
      />
    </>,
    <>
      <Typography variant="medium" component="p">
        Numǎr camion: {truck?.truckNumber}
      </Typography>
      <Typography variant="medium" component="p">
        Numǎr suporți scanați: {counter}
      </Typography>
    </>,
    <ScrollTopButton />
  ]

  if (isFinalizing) {
    items.push(
      <Button
        type="button"
        variant="contained"
        label="FINALIZEAZĂ"
        size="large"
        style={{ width: '100%' }}
        onClick={toggleIsFinalizing}
      />,
      <AlertModal
        open={isFinalizeModalOpen}
        handleClose={closeFinalizeModal}
        title={finalizeModalContent.title}
        description={finalizeModalContent.description}
        buttons={finalizeModalContent.buttons}
      />
    )
  }

  return (
    <Grid gridContainerStyle={{ marginTop: '20px' }} maxWidth="xs" items={items} itemsPerRow={1} />
  )
}

export default SupportScanStep1
